/**
 * Brio -- entry point for esbuild bundling.
 *
 * Components (self-registering custom elements):
 *   <brio-code-example>
 *   <brio-dialog>
 *   <brio-drawer>
 *   <brio-dropdown>
 *   <brio-tabs>
 *
 * Utilities (self-initializing, programmatic API exported):
 *   dismiss(element)        -- close a dismissible container programmatically
 *   dialog.open(idOrEl)     -- open a dialog modally; await returnValue at close
 *   drawer.open(id)         -- open a drawer by element ID (native showModal)
 *   drawer.close(id)        -- close a drawer with animated slide-out
 *   toast.show(opts)        -- show a toast (clones authored template)
 *   toast.adopt(el, opts)   -- orchestrate a caller-rendered toast element
 *   toast.dismiss(el)       -- dismiss a toast (animated)
 *
 * Base class (exported for extending):
 *   BrioBase                -- shared foundation for brio custom elements
 *
 * For selective imports, import individual modules directly:
 *   import "./brio-code-example.js";
 *   import "./brio-dialog.js";
 *   import "./brio-drawer.js";
 *   import "./brio-dropdown.js";
 *   import "./brio-tabs.js";
 *   import { dismiss } from "./dismiss.js";
 *   import { openDialog } from "./brio-dialog.js";
 *   import { openDrawer, closeDrawer } from "./brio-drawer.js";
 *   import { show, adopt, dismiss } from "./toast.js";
 *   import { BrioBase } from "./brio-base.js";
 *
 * For non-module contexts (inline handlers, script tags), use the
 * window global:
 *   brio.dismiss(element);
 *   brio.dialog.open("my-dialog");
 *   brio.drawer.open("my-drawer");
 *   brio.toast.show({ body: "Saved." });
 */

// --- Polyfills (load before everything else) ---

import './command.js';

// --- Components (self-registering custom elements) ---

import './brio-code-example.js';
import './brio-dialog.js';
import './brio-drawer.js';
import './brio-dropdown.js';
import './brio-tabs.js';

// --- Utilities (self-initializing + programmatic) ---

import { dismiss } from './dismiss.js';
import { openDialog } from './brio-dialog.js';
import { openDrawer, closeDrawer } from './brio-drawer.js';
import { show as toastShow, adopt as toastAdopt, dismiss as toastDismiss } from './toast.js';

// --- Re-exports for bundler consumers ---

export { dismiss };
export { openDialog };
export { openDrawer, closeDrawer };
export { toastShow, toastAdopt, toastDismiss };
export { BrioBase } from './brio-base.js';

// --- Global namespace (non-module contexts) ---
//
// Defensive: extras may load before core, in which case window.brio
// already exists with extras' namespaces populated. Merge core's
// entries into the existing object rather than overwriting it.

window.brio = window.brio || {};
window.brio.dismiss = dismiss;
window.brio.dialog = {
    open: openDialog,
};
window.brio.drawer = {
    open: openDrawer,
    close: closeDrawer,
};
window.brio.toast = {
    show: toastShow,
    adopt: toastAdopt,
    dismiss: toastDismiss,
};
